import React, { useEffect, useState } from "react";
import { Box, Checkbox, Grid, FormControlLabel, Tooltip, Typography } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';

interface FrequencyConfigProps {
    name: string,
    label: string,
    description: string,
    value: undefined | string,
    onChange: (ConfigTypeKey: string, AssignedValue: any) => void,
}

const BlankIcon = <Box sx={{ width: '9px', height: '9px', backgroundColor: '#C4C4C4' }}></Box>;
const CheckedIcon = <Box sx={{ width: '9px', height: '9px', backgroundColor: '#39A2DB' }}></Box>;
const LabelText = (label: string) => (
    <Typography 
        sx={{ 
            fontFamily: 'Work Sans',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '22px',
        }}
        >
        {label}
    </Typography>
)

interface ConfigStateType {
    [key: string]: boolean
}

export default function FrequencyConfig({
    name,
    label,
    description,
    value,
    onChange,
}: FrequencyConfigProps) {
    const [configState, setConfigState] = useState<ConfigStateType>({
        monthly: false,
        quarterly: false,
        semiAnnual: false,
        annual: false
    });

    useEffect(() => {
        setConfigState({
            monthly: value?.includes("'M'") ?? false,
            quarterly: value?.includes("'Q'") ?? false,
            semiAnnual: value?.includes("'S'") ?? false,
            annual: value?.includes("'A'") ?? false,
        });
    }, [value])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, configTypeKey?: string) => {
        const newState = {
            ...configState,
            [event.target.name]: event.target.checked
        }
        setConfigState(newState);

        const newValue = Object.keys(newState).reduce((str: string, key: string) => {
            if (newState[key]) {
                if (str) {
                    return `${str}, '${key.charAt(0).toUpperCase()}'`;
                }

                return `'${key.charAt(0).toUpperCase()}'`;
            }
            
            return str;
        }, "");

        onChange(name, newValue);
    };

    const checkboxControl = (name: string, checked: boolean) => (
        <Checkbox
            name={name}
            checked={checked}
            onChange={(event) => handleChange(event)}
            icon={BlankIcon}
            checkedIcon={CheckedIcon}
        />
    )

    return (
        <Grid container sx={{ alignItems: 'center', py: 1 }}>
            <Grid item xs={12} xl={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={description} placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: "#C4C4C4", cursor: "pointer", mr: '9px' }} />
                </Tooltip>

                {LabelText(label)}
            </Grid>

            <Grid item xs={12} xl={9} sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textAlign: { xs: 'right', xl: 'left' }
            }}>
                <Grid container>
                    <Grid item md={6} lg={3}>
                        <FormControlLabel 
                            control={checkboxControl('monthly', configState.monthly)}
                            label={LabelText('Monthly')}
                        />
                    </Grid>

                    <Grid item md={6} lg={3}>
                        <FormControlLabel 
                            control={checkboxControl('quarterly', configState.quarterly)}
                            label={LabelText('Quarterly')}
                        />
                    </Grid>

                    <Grid item md={6} lg={3.5}>
                        <FormControlLabel 
                            control={checkboxControl('semiAnnual', configState.semiAnnual)}
                            label={LabelText('Semi-Annual')}
                        />
                    </Grid>

                    <Grid item md={6} lg={2.5}>
                        <FormControlLabel 
                            control={checkboxControl('annual', configState.annual)}
                            label={LabelText('Annual')}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
